import React, { useEffect, useState, createContext } from 'react';

import SliderNavigation from './SliderNavigation/SliderNavigation';

import SlidesList from './SlideList/SlideList';

import analytics from 'src/utils/analytics/analytics';

interface SliderItem {
  id: string;
  caption: string;
  url: string;
}

type SlideDataProps = {
  data: SliderItem[];
};

type SliderItemsState = {
  isImageLoading: boolean;
} & SliderItem;

type SliderContextProps = {
  goToSlide: (number: number) => void;
  changeSlide: (direction: number) => void;
  slidesCount: number;
  slideNumber: number;
  items: SliderItem[];
};

// ---------------------------------- //

const initialSliderContext = {
  goToSlide: () => {},
  changeSlide: () => {},
  slidesCount: 0,
  slideNumber: 0,
  items: [],
};

export const SliderContext = createContext<SliderContextProps>(initialSliderContext);

// ---------------------------------- //

function Slider({ data }: SlideDataProps) {
  const [items, setItems] = useState<SliderItemsState[]>([]);
  const [slideNumber, setSlideNumber] = useState<number>(0);
  const [touchPosition, setTouchPosition] = useState(null);

  useEffect(() => {
    const modifiedItems = data.map((item, i) => ({ ...item, isImageLoading: true }));
    setItems(modifiedItems);
  }, [data]);

  const preloadImage = imgIndex => {
    if (items[imgIndex].isImageLoading) {
      const currentImg = new Image();
      currentImg.src = items[imgIndex].url;
      currentImg.onload = () => {
        setItems(items.map((item, i) => (i === imgIndex ? { ...item, isImageLoading: false } : item)));
      };
    }
  };

  const preloadImages = () => {
    const prevItemIndex = slideNumber - 1 < 0 ? items.length - 1 : slideNumber - 1;
    const nextItemIndex = (slideNumber + 1) % items.length;

    preloadImage(slideNumber);
    preloadImage(prevItemIndex);
    preloadImage(nextItemIndex);
  };

  useEffect(() => {
    if (items.length) {
      preloadImages();
    }
  }, [slideNumber, items]);

  useEffect(() => {
    if (items.length) {
      analytics.trackSlideViewed({
        slideId: items[slideNumber].id,
        slideImageUrl: items[slideNumber].url,
        slideNumber: slideNumber + 1,
      });
    }
  }, [slideNumber, items]);

  const changeSlide = (direction = 1) => {
    let slide = 0;

    if (slideNumber + direction < 0) {
      slide = items.length - 1;
    } else {
      slide = (slideNumber + direction) % items.length;
    }

    setSlideNumber(slide);
  };

  const goToSlide = (number: number) => {
    setSlideNumber(number % items.length);
  };

  const handleTouchStart = e => {
    const touchDown = e.touches[0].clientX;

    setTouchPosition(touchDown);
  };

  const handleTouchMove = e => {
    if (touchPosition === null) {
      return;
    }

    const currentPosition = e.touches[0].clientX;
    const direction = touchPosition - currentPosition;

    if (direction > 10) {
      changeSlide(1);
    }

    if (direction < -10) {
      changeSlide(-1);
    }

    setTouchPosition(null);
  };

  return (
    <div className="rcw-page-slider-wrapper" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
      <SliderContext.Provider
        value={{
          goToSlide,
          changeSlide,
          slidesCount: items.length,
          slideNumber,
          items,
        }}
      >
        <div className="rcw-page-slider">
          <SlidesList />
          <SliderNavigation />
        </div>
      </SliderContext.Provider>
    </div>
  );
}

export default Slider;
